export const DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE = 50;
export const DEFAULT_SELF_AUTH_LIST_VIEW_PAGINATION_MODEL = {
  page: 0,
  pageSize: DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE,
};
export const DEFAULT_SELF_AUTH_LIST_VIEW_SORT_MODEL = [];
export const SELF_AUTH_LIST_VIEW_PAGE_SIZE_OPTIONS = [50, 100, 250, 500];

const SELF_AUTH_LIST_VIEW_SORT_COLUMN_BY_FIELD = {
  activeStatus: 'IsActive',
  authorizationType: 'Type',
  extendedLoading: 'ExtendedLoading',
  locationDisplay: 'Location',
  locationIdn: 'Location',
  pidxCode: 'ProductPidx',
  productIdn: 'ProductIdn',
  productLongName: 'ProductName',
};
const getActiveStatusFilter = statusFilter => {
  const statusValue = String(statusFilter?.value || '').toLowerCase();

  switch (statusValue) {
    case 'allon':
      return 'Y';
    case 'alloff':
      return 'N';
    default:
      return null;
  }
};
const getTypeFilter = typeFilter => {
  const typeValue = String(typeFilter?.value || '').toLowerCase();

  switch (typeValue) {
    case 'base':
      return 'Base';
    case 'custom':
      return 'Custom';
    case 'supplier directed blend':
      return 'Supplier Directed Blend';
    default:
      return null;
  }
};
const toSortDirection = sortDirection => (sortDirection === 'desc' ? 'Descending' : 'Ascending');

const getSortParams = sortModel => {
  const activeSort = sortModel?.[0];
  const sortColumn = SELF_AUTH_LIST_VIEW_SORT_COLUMN_BY_FIELD[activeSort?.field];

  if (!sortColumn || !activeSort?.sort) {
    return {};
  }

  return { SortColumn: sortColumn, SortDirection: toSortDirection(activeSort.sort) };
};
const getPaginationParams = paginationModel => {
  const page = Number.isFinite(Number(paginationModel?.page)) ? Number(paginationModel.page) : 0;
  const results = SELF_AUTH_LIST_VIEW_PAGE_SIZE_OPTIONS.includes(Number(paginationModel?.pageSize))
    ? Number(paginationModel.pageSize)
    : DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE;

  return {
    ...(page !== 0 && { Page: page }),
    ...(results !== DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE && { Results: results }),
  };
};

const getMultiSelectValues = filterItems => {
  if (!Array.isArray(filterItems) || !filterItems.length) {
    return [];
  }

  return filterItems.map(item => item?.value).filter(Boolean);
};

export const buildSelfAuthListViewQueryParams = ({
  appliedFilters = {},
  paginationModel = DEFAULT_SELF_AUTH_LIST_VIEW_PAGINATION_MODEL,
  sortModel = [],
} = {}) => {
  const locationIds = getMultiSelectValues(appliedFilters?.location);
  const activeStatusFilter = getActiveStatusFilter(appliedFilters?.status);
  const typeFilter = getTypeFilter(appliedFilters?.type);
  const productIds = getMultiSelectValues(appliedFilters?.product);

  return {
    ...(appliedFilters?.affiliate?.customerIdName && {
      Affiliate: {
        CompanyCustomerIdName: appliedFilters.affiliate.customerIdName,
        DataCompanyId: appliedFilters.affiliate.dataCompanyId,
      },
    }),
    ...getPaginationParams(paginationModel),
    ...getSortParams(sortModel),
    // Encode quickFilter to handle special characters
    ...(activeStatusFilter !== null && { Status: activeStatusFilter }),
    ...(typeFilter && { SelfAuthorizationType: typeFilter }),
    ...(locationIds.length && { LocationIds: locationIds }),
    ...(productIds.length && { ProductIds: productIds }),
    ...(appliedFilters?.search &&
      appliedFilters.search.trim() && {
        quickFilter: appliedFilters.search.trim(),
      }),
  };
};

export const sanitizeSelfAuthQueryParams = queryParams => {
  if (Array.isArray(queryParams)) {
    return queryParams
      .map(sanitizeSelfAuthQueryParams)
      .filter(value => value !== undefined && value !== null && value !== '');
  }

  if (!queryParams || typeof queryParams !== 'object' || queryParams instanceof Date) {
    return queryParams;
  }

  return Object.entries(queryParams).reduce((accumulator, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return accumulator;
    }

    const sanitizedValue = sanitizeSelfAuthQueryParams(value);

    if (
      sanitizedValue === undefined ||
      sanitizedValue === null ||
      sanitizedValue === '' ||
      (typeof sanitizedValue === 'object' &&
        !Array.isArray(sanitizedValue) &&
        !Object.keys(sanitizedValue).length) ||
      (Array.isArray(sanitizedValue) && !sanitizedValue.length)
    ) {
      return accumulator;
    }

    return {
      ...accumulator,
      [key]: sanitizedValue,
    };
  }, {});
};

export const getSelfAuthListViewErrorMessage = err => {
  const status = err?.status || err?.response?.status;
  const errorTitle = err?.data?.title || err?.response?.data?.title || err?.message || 'an error';

  switch (status) {
    case 401:
    case 403:
      return `You don't have permission to view Self Authorizations.`;
    case 404:
      return 'Self authorizations could not be found for the selected affiliate.';
    default:
      return `Due to ${errorTitle}, your self authorizations could not be retrieved. Please reload the page to try again.`;
  }
};

const toCamelCaseKey = key => {
  if (!key) {
    return key;
  }

  return `${key.charAt(0).toLowerCase()}${key.slice(1)}`;
};

const getResponseValue = (data, key, fallbackValue) =>
  data?.[key] ?? data?.[toCamelCaseKey(key)] ?? fallbackValue;

const formatIdnAndLongName = (idn, longName, fallbackValue = '') => {
  const normalizedLongName = longName || fallbackValue || '';

  if (!idn) {
    return normalizedLongName;
  }

  if (!normalizedLongName) {
    return idn;
  }

  if (normalizedLongName === idn || normalizedLongName.startsWith(`${idn} `)) {
    return normalizedLongName;
  }

  return `${idn} ${normalizedLongName}`;
};

const getPidxCode = product =>
  product.pidxCode ||
  product.PidxCode ||
  product.pidx ||
  product.Pidx ||
  product.pidxId ||
  product.PidxId ||
  product.pidxIdName ||
  product.PidxIdName ||
  '';

const normalizeSupplierDetails = details => {
  if (!Array.isArray(details)) {
    return [];
  }

  return details.map((detail, index) => {
    const supplierName = getResponseValue(detail, 'SupplierName', '');
    const authorizationType = getResponseValue(detail, 'AuthType', '');

    return {
      authorizationType,
      id: `${index}-${supplierName}`,
      supplierName,
    };
  });
};

const toSelfAuthListViewRows = data => {
  const authorizations = getResponseValue(data, 'Authorizations', []);

  return authorizations.map((item, index) => {
    const location = getResponseValue(item, 'Location', {});
    const product = getResponseValue(item, 'Product', {});
    const productIdn = getResponseValue(product, 'Idn', '');
    const productLongName = getResponseValue(product, 'Name', '');
    const locationIdn = getResponseValue(location, 'Idn', '');
    const locationName = getResponseValue(location, 'Name', '');
    const locationDisplayName =
      getResponseValue(location, 'DisplayName', '') ||
      formatIdnAndLongName(locationIdn, locationName);
    const authorizationType = getResponseValue(item, 'Type', '');
    const supplierCount = Number(getResponseValue(item, 'SupplierCount', 0)) || 0;
    const suppliers = normalizeSupplierDetails(getResponseValue(item, 'SupplierDetails', []));
    const extendedLoadingAllowed = Boolean(getResponseValue(item, 'ExtendedLoadingAllowed'));
    const extendedLoadingActivated = Boolean(getResponseValue(item, 'ExtendedLoadingActivated'));

    const isActive = getResponseValue(item, 'IsActive', null);
    const productLocationAuthorizationId = getResponseValue(
      item,
      'ProductLocationAuthorizationId',
      null
    );

    return {
      activeStatus: isActive,
      authorizationType,
      extendedLoading: extendedLoadingAllowed,
      extendedLoadingActivated,
      id: `${locationIdn || 'location'}-${productIdn || 'product'}-${
        authorizationType || 'type'
      }-${index}`,
      locationDisplay: locationDisplayName,
      locationId: location.id,
      locationIdn,
      locationName,
      pidxCode: getResponseValue(product, 'PidxCode', getPidxCode(product)),
      productId: getResponseValue(product, 'Id', ''),
      productIdn,
      productLocationAuthorizationId,
      productLongName,
      supplierCount,
      suppliers,
    };
  });
};

export const buildSelfAuthListViewResult = data => {
  const mappedRows = toSelfAuthListViewRows(data);

  return {
    rows: mappedRows,
    total: getResponseValue(data, 'AuthorizationCount', mappedRows.length),
  };
};
